import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { Table, TableHead, TableRow, TableHeader, TableBody, Loadable } from '@ctct/ui-react';

import SMSTiersTableRow from './SMSTiersTableRow';
import './_SMSTiersTable.scss';

const SMSTiersTable = ({
  currency,
  setSelectedSMSTier,
  SMSTiers,
  contactCountLowEnd,
  selectedSMSTier,
  t,
  loading,
  initiallySelectedTierMin,
  allDisabled,
}) => {
  useEffect(() => {
    // set initially selected tier to match the recommended tier
    if (SMSTiers && contactCountLowEnd != null && SMSTiers.length && !selectedSMSTier) {
      const contactCountLowEndTimesTwo = contactCountLowEnd * 2;

      for (let i = 0; ; i += 1) {
        const { tierMax, tierMin, price } = SMSTiers[i];

        if (
          i + 1 === SMSTiers.length ||
          (contactCountLowEndTimesTwo >= tierMin && (tierMax === -1 || contactCountLowEndTimesTwo <= tierMax))
        ) {
          setSelectedSMSTier(tierMin, tierMax, price);
          break;
        }
      }
    }
  }, [SMSTiers]);

  const [scrolledIntoView, setScrolledIntoView] = useState(false);

  const platformExpression = /Mac|iPhone|iPod|iPad/i;
  const rejectedExpression = /Chrome|Android|CriOS|FxiOS|EdgiOS/i;
  const expectedExpression = /Safari/i;

  const isAppleSafari = () => {
    const agent = navigator.userAgent;

    if (rejectedExpression.test(agent)) {
      return false;
    }

    return platformExpression.test(agent) && expectedExpression.test(agent);
  };

  const currentTier = (SMSTiers || []).find(
    ({ tierMin }) => initiallySelectedTierMin != null && initiallySelectedTierMin === tierMin
  );

  const recommendedTier = (SMSTiers || []).find(
    ({ tierMin, tierMax }) => contactCountLowEnd * 2 >= tierMin && (tierMax === -1 || contactCountLowEnd * 2 <= tierMax)
  );

  const showCurrentBadge = (tierMin) => currentTier?.tierMin === tierMin;

  // show recommended badge if row matches recommended tier and recommended tier is higer than current tier
  const showRecommendedBadge = (tierMin) =>
    recommendedTier?.tierMin === tierMin && recommendedTier?.tierMin > currentTier?.tierMin;

  return (
    <span className={classNames('sms-tiers-table-wrapper', isAppleSafari() && 'on-safari')}>
      <Table className="table-head-container">
        <TableHead>
          <TableRow>
            <TableHeader scope="col" className="sms-column1">
              {t('sms-plans.select-header')}
            </TableHeader>
            <TableHeader scope="col" className="sms-column2">
              {t('sms-plans.number-of-messages-header')}
            </TableHeader>
            <TableHeader scope="col" className="sms-column3">
              {t('sms-plans.price-month-header')}
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody className="hidden-body">
          {SMSTiers.map(({ tierMin, tierMax, price }) => (
            <SMSTiersTableRow
              key={`sms-row-${tierMin}`}
              min={tierMin}
              max={tierMax}
              price={price}
              isDisabled={false}
              initiallySelectedTier={false}
              selectedSMSTierMin={selectedSMSTier ? selectedSMSTier.min : -1}
              selectTierCallback={setSelectedSMSTier}
              currency={currency}
              contactCountLowEnd={contactCountLowEnd}
              scrolledIntoView={scrolledIntoView}
              setScrolledIntoView={setScrolledIntoView}
              t={t}
            />
          ))}
        </TableBody>
      </Table>
      <div className="sms-tiers-table sms-tier-table-border">
        <Loadable loading={loading}>
          <Table>
            <TableHead className="hidden-head">
              <TableRow>
                <TableHeader scope="col" className="sms-column1">
                  {t('sms-plans.select-header')}
                </TableHeader>
                <TableHeader scope="col" className="sms-column2">
                  {t('sms-plans.number-of-messages-header')}
                </TableHeader>
                <TableHeader scope="col" className="sms-column3">
                  {t('sms-plans.price-month-header')}
                </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {SMSTiers.map(({ tierMin, tierMax, price }) => (
                <SMSTiersTableRow
                  key={`sms-row-${tierMin}`}
                  min={tierMin}
                  max={tierMax}
                  price={price}
                  selectedSMSTierMin={selectedSMSTier ? selectedSMSTier.min : -1}
                  selectTierCallback={setSelectedSMSTier}
                  currency={currency}
                  isDisabled={allDisabled}
                  contactCountLowEnd={contactCountLowEnd}
                  scrolledIntoView={scrolledIntoView}
                  setScrolledIntoView={setScrolledIntoView}
                  visibleTableRow
                  t={t}
                  showCurrentBadge={showCurrentBadge(tierMin)}
                  showRecommendedBadge={showRecommendedBadge(tierMin)}
                />
              ))}
            </TableBody>
          </Table>
        </Loadable>
      </div>
    </span>
  );
};

SMSTiersTable.defaultProps = {
  loading: false,
  initiallySelectedTierMin: null,
  allDisabled: false,
  selectedSMSTier: null,
};

SMSTiersTable.propTypes = {
  t: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  contactCountLowEnd: PropTypes.number.isRequired,
  setSelectedSMSTier: PropTypes.func.isRequired,
  selectedSMSTier: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number,
    price: PropTypes.number,
  }),
  initiallySelectedTierMin: PropTypes.number,
  SMSTiers: PropTypes.arrayOf(
    PropTypes.shape({
      tierMin: PropTypes.number.isRequired,
      tierMax: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
    })
  ).isRequired,
  loading: PropTypes.bool,
  allDisabled: PropTypes.bool,
};

export default SMSTiersTable;
